/**
 * GLB 字节 + 解析后 scene 的内存缓存（LRU + in-flight 去重）。
 *
 * 背景：ModelViewer 与 lib/convert（下载转 STL/3MF）原本各自 fetch + parse GLB，
 * 导致：
 *  - 同一模型重复打开 → 重新 fetch + parse（几 MB 重传 + 解析耗时）。
 *  - 「先看后下载」→ GLB 被拉两次、解析两次。
 *  - 并发打开同一模型 → 双倍请求。
 *
 * 本模块统一这两条路径：缓存以 glbUrl 为 key，存 { bytes, scene }。
 * in-flight Map 去重并发请求；LRU + 总字节阈值（默认 200MB）防内存膨胀。
 *
 * 鉴权与解析逻辑与旧 ModelViewer.loadAuthedGlbScene / convert.loadGlbScene
 * 完全一致（getToken + Bearer + GLTFLoader.parseAsync）。
 */

import type { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import type { Group } from "three";
import { getToken } from "./api";
import i18n from "../i18n";

/** 单条缓存项：字节（下载/格式转换用）+ scene（渲染用，按需解析） */
interface CacheEntry {
  bytes: ArrayBuffer;
  /** scene 懒解析：只有 getGlbScene 调过才存在。bytes 已就绪即可服务下载。 */
  scene: Group | null;
  /** 插入顺序（用于 LRU 淘汰） */
  order: number;
}

/** 进行中的请求（避免并发重复拉取同一 url） */
interface InflightEntry {
  /** 字节拉取 promise */
  bytesPromise: Promise<ArrayBuffer>;
  /** scene 解析 promise（懒创建：仅 getGlbScene 触发） */
  scenePromise: Promise<Group> | null;
}

/** 缓存上限（字节）。200MB：约 10-40 个典型 GLB，覆盖浏览/历史常用集。 */
const MAX_BYTES = 200 * 1024 * 1024;

/**
 * 解析后 scene 的内存成本系数（P2-11）：three.js 把压缩 GLB 解成显式
 * BufferAttribute/纹理后，常驻内存是压缩字节数的数倍。预算按
 * bytes × 3 计价已解析条目，让 LRU 更早淘汰带 scene 的条目，
 * 长会话内存才有真实上界（移动端 Safari 无上界会崩溃）。
 */
const SCENE_COST_MULTIPLIER = 3;

const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, InflightEntry>();
let lruCounter = 0;

// GLTFLoader 动态导入（懒初始化）：
// - 本模块被首页/广场静态引用（prefetchGlb 只做 fetch），若顶层 import GLTFLoader
//   会把整个 three chunk 拖进首屏关键路径（~700KB 无用 JS）。
// - 懒初始化也让测试可在 import 本模块前完成 vi.mock。
import type { GLTFLoader as GLTFLoaderT } from "three/examples/jsm/loaders/GLTFLoader.js";

let sharedLoader: GLTFLoaderT | null = null;
async function getLoader(): Promise<GLTFLoaderT> {
  if (!sharedLoader) {
    const { GLTFLoader } = await import(
      "three/examples/jsm/loaders/GLTFLoader.js"
    );
    sharedLoader = new GLTFLoader();
  }
  return sharedLoader;
}

/** 条目占用的内存预算（字节）：原始字节 + 已解析 scene 的放大成本 */
function entryCost(e: CacheEntry): number {
  return e.bytes.byteLength * (e.scene ? SCENE_COST_MULTIPLIER : 1);
}

/** 当前缓存总预算（用于 LRU 阈值判断） */
function totalBytes(): number {
  let sum = 0;
  for (const e of cache.values()) sum += entryCost(e);
  return sum;
}

interface Disposable {
  dispose: () => void;
}

/** 结构化判定可释放对象（不 import three 运行时——本模块在首屏 chunk） */
function isDisposable(v: unknown): v is Disposable {
  return (
    typeof v === "object" &&
    v !== null &&
    typeof (v as { dispose?: unknown }).dispose === "function"
  );
}

/**
 * 释放 scene 持有的 GPU 资源（geometry/material/texture，P2-11）。
 * 旧实现淘汰只 cache.delete，几何体/材质不 dispose——JS 堆引用断开后
 * GPU 侧资源仍占显存，长会话浏览广场/历史内存无上界。
 *
 * 走结构化 duck-typing 而非 import three：本模块被首页静态引用，
 * 运行时 import three 会把 ~700KB 拖进首屏关键路径（见 getLoader 注释）。
 *
 * 已知取舍：若某个仍被挂载的 ModelViewer 正在渲染该 scene（超出预算
 * 淘汰的极端场景），dispose 后该视图会失效——相比无上界内存（移动端
 * Safari 崩溃）这是更安全的默认；重新打开模型会重新拉取解析。
 */
function disposeEntryScene(scene: Group | null): void {
  // 防御非 three 对象（测试 mock / 半解析对象）：traverse 不可用即放弃释放
  if (!scene || typeof (scene as { traverse?: unknown }).traverse !== "function") {
    return;
  }
  scene.traverse((obj) => {
    const o = obj as { geometry?: unknown; material?: unknown };
    if (isDisposable(o.geometry)) o.geometry.dispose();
    const materials = Array.isArray(o.material) ? o.material : [o.material];
    for (const mat of materials) {
      if (mat == null) continue;
      // 材质上的贴图（map/normalMap/…）：按 isTexture 特征统一识别
      for (const value of Object.values(mat as Record<string, unknown>)) {
        if (
          typeof value === "object" &&
          value !== null &&
          (value as { isTexture?: boolean }).isTexture === true &&
          isDisposable(value)
        ) {
          value.dispose();
        }
      }
      if (isDisposable(mat)) mat.dispose();
    }
  });
}

/** LRU 淘汰：直到总预算 ≤ MAX_BYTES。按 order（插入顺序）淘汰最旧的。 */
function evictIfNeeded(): void {
  while (totalBytes() > MAX_BYTES && cache.size > 0) {
    // Map 保持插入顺序 → 第一个即最旧（除非被 get 触发重排序，见 touch）
    let oldestKey: string | null = null;
    let oldestOrder = Infinity;
    for (const [k, v] of cache) {
      if (v.order < oldestOrder) {
        oldestOrder = v.order;
        oldestKey = k;
      }
    }
    if (oldestKey) {
      const evicted = cache.get(oldestKey);
      if (evicted) disposeEntryScene(evicted.scene);
      cache.delete(oldestKey);
      inflight.delete(oldestKey);
    } else {
      break;
    }
  }
}

/** 标记最近访问（LRU 排序） */
function touch(entry: CacheEntry): void {
  entry.order = ++lruCounter;
}

/**
 * 鉴权拉取 GLB 失败：携带 HTTP 状态码与后端错误码（如广场下载的
 * download_requires_subscription / insufficient_credits），供上层按码引导
 * （订阅提示等）。message 与旧版一致（i18n 文案），仅消费 message 的调用方不受影响。
 */
export class GlbFetchError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(message: string, status: number, code: string | null) {
    super(message);
    this.name = "GlbFetchError";
    this.status = status;
    this.code = code;
  }
}

/**
 * 从错误响应中提取后端错误码（{error: "..."}）。
 * body 非 JSON（如网关错误页）或无法解析时返回 null，不影响错误抛出。
 */
async function extractErrorCode(resp: Response): Promise<string | null> {
  try {
    const data = (await resp.json()) as { error?: unknown } | null;
    if (data && typeof data.error === "string") return data.error;
  } catch {
    // 非 JSON body：无错误码
  }
  return null;
}

/**
 * 鉴权拉取 GLB 字节，返回 ArrayBuffer。
 * - 非 2xx：抛 GlbFetchError（message 为 i18n 文案，携带 status + 后端 error 码）
 */
async function fetchGlbBytesAuthed(
  glbUrl: string,
  onProgress?: (loaded: number, total: number | null) => void,
): Promise<ArrayBuffer> {
  const token = await getToken();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const resp = await fetch(glbUrl, { headers });
  if (!resp.ok) {
    const code = await extractErrorCode(resp);
    const message =
      resp.status === 401
        ? i18n.t("errors.viewerUnauthorized")
        : i18n.t("errors.viewerLoadFailedStatus", { status: resp.status });
    throw new GlbFetchError(message, resp.status, code);
  }

  // 有 content-length 走流式读取驱动进度；否则整体 arrayBuffer（无进度）。
  const contentLength = resp.headers.get("content-length");
  const total = contentLength ? parseInt(contentLength, 10) : null;

  if (!resp.body || !onProgress) {
    const buf = await resp.arrayBuffer();
    if (onProgress) onProgress(buf.byteLength, total ?? buf.byteLength);
    return buf;
  }

  // 流式读取并聚合，触发进度回调
  const reader = resp.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      loaded += value.byteLength;
      onProgress(loaded, total);
    }
  }
  // 合并 chunks → ArrayBuffer
  const merged = new Uint8Array(loaded);
  let offset = 0;
  for (const c of chunks) {
    merged.set(c, offset);
    offset += c.byteLength;
  }
  return merged.buffer;
}

/** 把字节解析成 scene（懒解析：getGlbBytes 不触发，getGlbScene 才触发） */
async function parseScene(bytes: ArrayBuffer): Promise<Group> {
  const loader = await getLoader();
  const gltf: GLTF = await loader.parseAsync(bytes, "");
  gltf.scene.updateMatrixWorld(true);
  return gltf.scene;
}

/**
 * 确保字节已下载并写入缓存，返回缓存条目。
 * - 命中缓存：直接返回（touch 更新 LRU）
 * - in-flight 命中：等待同一下载完成
 * - 新请求：fetch → 写缓存 → 返回
 *
 * 这是 getGlbScene / getGlbBytes 共享的底层。scene 解析不在此层触发（懒解析）。
 */
async function ensureBytes(
  glbUrl: string,
  onProgress?: (loaded: number, total: number | null) => void,
): Promise<CacheEntry> {
  // 1. 缓存命中
  const cached = cache.get(glbUrl);
  if (cached) {
    touch(cached);
    return cached;
  }

  // 2. in-flight 命中：等待字节就绪后返回缓存条目
  const existing = inflight.get(glbUrl);
  if (existing) {
    await existing.bytesPromise;
    const e = cache.get(glbUrl);
    if (e) {
      touch(e);
      return e;
    }
    // 极端：await 期间被 LRU 淘汰 → 递归重新拉取（罕见）
    return ensureBytes(glbUrl);
  }

  // 3. 新请求：fetch 字节，写入缓存（scene 留空，懒解析）
  const bytesPromise = (async () => {
    const bytes = await fetchGlbBytesAuthed(glbUrl, onProgress);
    const entry: CacheEntry = { bytes, scene: null, order: ++lruCounter };
    cache.set(glbUrl, entry);
    evictIfNeeded();
    return bytes;
  })();

  inflight.set(glbUrl, { bytesPromise, scenePromise: null });

  // 下载失败清理 in-flight（允许后续重试）。挂静默 catch 防 unhandled rejection。
  bytesPromise.catch(() => {
    if (inflight.get(glbUrl)?.bytesPromise === bytesPromise) {
      inflight.delete(glbUrl);
    }
  });

  await bytesPromise;
  const entry = cache.get(glbUrl);
  if (entry) {
    touch(entry);
    return entry;
  }
  return ensureBytes(glbUrl);
}

/**
 * 获取 GLB scene（解析后的 three.js Group），命中缓存秒回。
 * - 命中且 scene 已解析：直接返回
 * - 命中但 scene 未解析：复用 bytes 解析（不重新下载），并通过 in-flight
 *   scenePromise 去重并发解析（避免同一模型被并发 parseAsync 两次）
 * - 未命中：fetch → 缓存 bytes → 解析 scene
 *
 * @param onProgress 下载进度回调（loaded, total|null）。仅首次下载时触发。
 */
export async function getGlbScene(
  glbUrl: string,
  onProgress?: (loaded: number, total: number | null) => void,
): Promise<Group> {
  const entry = await ensureBytes(glbUrl, onProgress);
  if (entry.scene) return entry.scene;

  // 字节已就绪但 scene 未解析。用 in-flight scenePromise 去重并发解析。
  const existing = inflight.get(glbUrl);
  if (existing) {
    // 复用已在进行的解析（若有），避免并发 parseAsync
    if (!existing.scenePromise) {
      existing.scenePromise = parseScene(entry.bytes).then((scene) => {
        entry.scene = scene;
        return scene;
      });
      // 解析失败清理，允许重试
      existing.scenePromise.catch(() => {
        if (inflight.get(glbUrl) === existing) existing.scenePromise = null;
      });
    }
    return existing.scenePromise;
  }

  // 无 in-flight（下载已完成、inflight 已清除）：直接解析
  const scene = await parseScene(entry.bytes);
  entry.scene = scene;
  return scene;
}

/**
 * 获取 GLB 原始字节（用于下载/格式转换），命中缓存秒回。
 * - 若 scene 已在缓存（预览过），bytes 一定也在 → 零下载
 * - 否则走 fetch（写入缓存，scene 留空，供后续 getGlbScene 复用）
 *
 * 注意：不解析 scene（省 CPU），仅保证字节就绪。
 */
export async function getGlbBytes(
  glbUrl: string,
  onProgress?: (loaded: number, total: number | null) => void,
): Promise<ArrayBuffer> {
  const entry = await ensureBytes(glbUrl, onProgress);
  return entry.bytes;
}

/**
 * 预取 GLB 到缓存（用于 hover 预热）。
 * 静默失败（网络/鉴权错误不上抛），失败时仅清理 in-flight。
 */
export function prefetchGlb(glbUrl: string): void {
  if (cache.has(glbUrl) || inflight.has(glbUrl)) return;
  // 静默：预取不应阻塞用户交互或抛错
  getGlbBytes(glbUrl).catch(() => {
    /* 预取失败静默忽略，点击时正式加载会再试 */
  });
}

/** 清除全部缓存（测试 / 手动释放用）；scene 的 GPU 资源一并释放 */
export function clearModelCache(): void {
  for (const e of cache.values()) disposeEntryScene(e.scene);
  cache.clear();
  inflight.clear();
}

/** 当前缓存条目数（测试用） */
export function modelCacheSize(): number {
  return cache.size;
}
